import React from "react";
import { Box } from "@mui/material";
import { useWindowSize } from "@uidotdev/usehooks";

// Template183: Menú/Plan de viaje de Kioto (solo lectura, mismo aspecto que Template50/Tokio)
// Ítems clicables que navegan a la página correspondiente. Sin UI de edición.
const Template183 = ({
	backgroundColor,
	textColor,
	headerBackgroundColor,
	headerTextColor,
	fontFamily,
	page,
	updatePage,
	onNavigateToPage,
	pages = [],
}) => {
	const size = useWindowSize();
	const isSmallScreen = size.width <= 600;

	// Primera página de cada día en la guía Kioto (por templateId)
	const diasTemplateMapping = [25, 29, 27, 33, 36]; // Kiyomizudera, Pabellón, Arashiyama, Fushimi, Extra
	// Kioto usa 184 Transporte, 187 Actividades, 188 Aeropuertos (no 37, 39, 40 como Tokio)
	const extrasTemplateMapping =
		Array.isArray(page?.data?.extrasTemplateMapping) &&
		page.data.extrasTemplateMapping.length > 0
			? page.data.extrasTemplateMapping
			: [184, 38, 187, 188, 41, 43]; // Transporte, JRPass, Actividades, Aeropuertos, Consejos, Kofi

	const findPageByTemplateId = (templateId) => {
		return pages.find(
			(p) => p.templateId === templateId || p.data?.templateId === templateId,
		);
	};

	// Para navegación: usar id si existe, si no el índice en pages (guías prebuilt)
	const getPageIdOrIndex = (targetPage) => {
		if (!targetPage) return null;
		const id = targetPage?.id ?? targetPage?.data?.id;
		if (id != null) return id;
		const idx = pages.indexOf(targetPage);
		return idx >= 0 ? idx : null;
	};

	const pageData = page?.data || {};
	const diasRaw =
		pageData.dias && pageData.dias.length > 0 ? pageData.dias : null;
	const extrasRaw =
		pageData.extras && pageData.extras.length > 0 ? pageData.extras : null;

	const dias = diasRaw.map((dia, index) => {
		const templateId = diasTemplateMapping[index];
		const targetPage = templateId ? findPageByTemplateId(templateId) : null;
		return {
			...dia,
			pageId: dia.pageId ?? getPageIdOrIndex(targetPage),
		};
	});

	const extras = extrasRaw.map((extra, index) => {
		const templateId =
			index >= 0 && index < extrasTemplateMapping.length
				? extrasTemplateMapping[index]
				: null;
		const targetPage = templateId ? findPageByTemplateId(templateId) : null;
		return {
			...extra,
			pageId: extra.pageId ?? getPageIdOrIndex(targetPage),
		};
	});

	const headerText = page?.texts?.[0]?.content || "Plan de viaje";
	const logoImage =
		page?.canvasImages?.find((img) => img.id === "logo")?.imageUrl ||
		"/images/logo.png";

	const actualHeaderColor = headerBackgroundColor || "#5ba374";

	const handleNavigate = (pageId) => {
		if (onNavigateToPage && pageId != null) {
			onNavigateToPage(pageId);
		}
	};

	return (
		<Box
			sx={{
				display: "flex",
				flexDirection: "column",
				minHeight: "100%",
				height: "auto",
				width: "100%",
				backgroundColor: backgroundColor || "#f7f7f7",
				fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				position: "relative",
				"& *": {
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				},
			}}
		>
			{/* CABECERA */}
			<Box
				data-block-id="header"
				data-template-header
				sx={{
					backgroundColor: actualHeaderColor,
					color: headerTextColor || "#fff",
					textAlign: "center",
					padding: "15px 0",
					fontSize: "14px",
					fontWeight: 800,
					position: "sticky",
					top: 0,
					zIndex: 1,
					fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
				}}
			>
				{headerText}
				<Box
					component="img"
					src={logoImage}
					alt="logo"
					sx={{
						width: isSmallScreen ? 66 : 80,
						position: "absolute",
						right: 2,
						top: 18,
						zIndex: 999999,
					}}
				/>
			</Box>

			{/* CONTENIDO */}
			<Box data-block-id="text-1" sx={{ padding: isSmallScreen ? "10px 10px" : "20px 16px" }}>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						gap: isSmallScreen ? "10px" : "20px",
						flexWrap: "nowrap",
					}}
				>
					{dias.map((dia, index) => (
						<Box
							key={index}
							data-block-id={`text-${index + 2}`}
							onClick={() => {
								if (dia.pageId != null) {
									handleNavigate(dia.pageId);
								}
							}}
							sx={{
								borderRadius: "12px",
								padding: 0,
								display: "flex",
								alignItems: "center",
								gap: isSmallScreen ? "5px" : "10px",
								position: "relative",
								minHeight: 30,
								width: "auto",
								cursor: dia.pageId != null ? "pointer" : "default",
								transition: "transform 0.3s ease",
								marginLeft: "20px",
								"&:hover":
									dia.pageId != null
										? {
												transform: "translateY(-5px)",
												"& .box": {
													boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
												},
											}
										: {},
							}}
						>
							{dia.image && (
								<Box
									component="img"
									src={dia.image}
									alt=""
									sx={{
										width: 45,
										zIndex: 100,
										position: "absolute",
										left: -20,
									}}
								/>
							)}
							<Box
								className="box"
								sx={{
									backgroundColor: "#fff",
									borderRadius: "12px",
									padding: "18px 16px",
									display: "flex",
									alignItems: "center",
									gap: "10px",
									position: "relative",
									minHeight: 30,
									width: "100%",
									marginLeft: 0,
									textAlign: "left",
									height: "auto",
									flex: 1,
									transition: "box-shadow 0.3s ease",
									paddingLeft: "16px",
								}}
							>
								<Box
									sx={{
										fontSize: "13px !important",
										fontWeight: 600,
										color: "#333",
										marginLeft: "20px",
										fontFamily: `${
											fontFamily || "'Poppins', sans-serif"
										} !important`,
										whiteSpace: "normal",
									}}
								>
									Día {index + 1}:{" "}
									{isSmallScreen && dia.tituloMovil
										? typeof dia.tituloMovil === "string"
											? dia.tituloMovil
											: dia.titulo
										: dia.titulo}
								</Box>
							</Box>
						</Box>
					))}
				</Box>

				<Box
					component="h3"
					sx={{
						fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
						fontSize: 12,
						fontWeight: 700,
						margin: 0,
						textTransform: "uppercase",
						marginTop: 2,
						marginBottom: 1,
					}}
				>
					Información útil
				</Box>

				{/* Escritorio: fila de tarjetas como Template129 */}
				{!isSmallScreen ? (
					<Box
						data-block-id="text-extras"
						sx={{
							display: "flex",
							flexDirection: "row",
							flexWrap: "nowrap",
							gap: "12px",
							alignItems: "stretch",
							justifyContent: "stretch",
							width: "100%",
							pb: 0.5,
						}}
					>
						{extras.map((extra, index) => (
							<Box
								key={index}
								data-block-id={`text-extra-${index + 1}`}
								onClick={() => {
									if (extra.pageId != null) handleNavigate(extra.pageId);
								}}
								sx={{
									flex: "1 1 0",
									minWidth: 0,
									minHeight: 100,
									backgroundColor: "#fff",
									borderRadius: "12px",
									display: "flex",
									flexDirection: "column",
									alignItems: "center",
									justifyContent: "center",
									padding: "10px 8px",
									cursor: extra.pageId != null ? "pointer" : "default",
									transition: "transform 0.2s ease, box-shadow 0.2s ease",
									boxShadow: "0 1px 4px rgba(0,0,0,0.08)",
									"&:hover":
										extra.pageId != null
											? {
													transform: "translateY(-3px)",
													boxShadow: "0 4px 12px rgba(0, 0, 0, 0.12)",
											  }
											: {},
								}}
							>
								{extra.image && (
									<Box
										component="img"
										src={extra.image}
										alt=""
										sx={{
											width: 54,
											height: 54,
											objectFit: "contain",
										}}
									/>
								)}
								<Box
									sx={{
										width: "100%",
										minHeight: "2.5em",
										display: "flex",
										justifyContent: "center",
										alignItems: "center",
									}}
								>
									<Box
										component="span"
										sx={{
											fontSize: "11px",
											fontWeight: 600,
											color: "#333",
											textAlign: "center",
											lineHeight: 1.25,
											whiteSpace: "normal",
											display: "-webkit-box",
											WebkitLineClamp: 2,
											overflow: "hidden",
											wordBreak: "break-word",
											fontFamily: `${fontFamily || "'Poppins', sans-serif"} !important`,
										}}
									>
										{extra.titulo}
									</Box>
								</Box>
							</Box>
						))}
					</Box>
				) : (
					<Box
						data-block-id="text-extras-mobile"
						sx={{
							display: "flex",
							flexDirection: "column",
							gap: "20px",
							flexWrap: "nowrap",
							paddingBottom: "2px",
							overflowY: "visible",
						}}
					>
						{extras.map((extra, index) => (
							<Box
								key={index}
								data-block-id={`text-extra-${index + 1}`}
								onClick={() => {
									if (extra.pageId != null) {
										handleNavigate(extra.pageId);
									}
								}}
								sx={{
									borderRadius: "12px",
									padding: 0,
									display: "flex",
									alignItems: "center",
									gap: "10px",
									position: "relative",
									minHeight: 30,
									width: "auto",
									cursor: extra.pageId != null ? "pointer" : "default",
									transition: "transform 0.3s ease",
									marginLeft: "20px",
									"&:hover":
										extra.pageId != null
											? {
													transform: "translateY(-5px)",
													"& .box": {
														boxShadow: "0 4px 12px rgba(0, 0, 0, 0.1)",
													},
											  }
											: {},
								}}
							>
								{extra.image && (
									<Box
										component="img"
										src={extra.image}
										alt=""
										sx={{
											width: 45,
											zIndex: 100,
											position: "absolute",
											left: -20,
										}}
									/>
								)}
								<Box
									className="box"
									sx={{
										backgroundColor: "#fff",
										borderRadius: "12px",
										padding: "18px 16px",
										display: "flex",
										alignItems: "center",
										gap: "10px",
										position: "relative",
										minHeight: 30,
										width: "100%",
										marginLeft: 0,
										textAlign: "left",
										height: "auto",
										flex: 1,
										transition: "box-shadow 0.3s ease",
										paddingLeft: "16px",
									}}
								>
									<Box
										sx={{
											fontSize: "13px !important",
											fontWeight: 600,
											color: "#333",
											marginLeft: "20px",
											fontFamily: `${
												fontFamily || "'Poppins', sans-serif"
											} !important`,
										}}
									>
										{extra.titulo}
									</Box>
								</Box>
							</Box>
						))}
					</Box>
				)}
			</Box>
		</Box>
	);
};

Template183.thumbnail = "/images/template-1.png";

export default Template183;
